import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useNavigate } from 'react-router'
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import CertDocumentUpload from '../CertDocumentUpload'
import GuidedDiveRequirements from '../GuidedDiveRequirements'
import { useSessionStore } from '../../state'
import {
  submitAccreditation,
  resubmitAccreditation
} from '../../services/accreditationService'
import Database from '../../types/Database'
import routes from '../../pages/routes'
import {
  PROVIDERS,
  PROVIDER_LABELS,
  getLevelsForProvider,
  AccreditationProvider,
  providerRequiresEmail,
  isDivemasterLevel
} from '../../constants/accreditationLevels'

export interface AccreditationFormProps {
  mode: 'submit' | 'resubmit'
}

interface FormValues {
  type: AccreditationProvider | ''
  level: string
  fullName: string
  dateOfBirth: string
  certificationNumber: string
  optionalMemberId: string
  registeredEmail: string
}

const AccreditationForm = ({ mode }: AccreditationFormProps) => {
  const navigate = useNavigate()
  const userId = useSessionStore(s => s.user?.uid)
  const profile = useSessionStore(s => s.profile)
  const setProfile = useSessionStore(s => s.setProfile)
  const documentUrlRef = useRef<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState(false)

  // Guided dive professional docs (populated by GuidedDiveRequirements child)
  const guidedDiveDocsRef = useRef<Database.GuidedDiveSubmissionDocs | null>(
    null
  )

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors }
  } = useForm<FormValues>({
    defaultValues: {
      type: '',
      level: '',
      fullName: profile?.displayName ?? '',
      dateOfBirth: '',
      certificationNumber: '',
      optionalMemberId: '',
      registeredEmail: ''
    }
  })

  const selectedType = watch('type')
  const selectedLevel = watch('level')

  // Guided dive toggle state
  const [guidedDiveToggle, setGuidedDiveToggle] = useState(false)
  const showGuidedDiveToggle = isDivemasterLevel(selectedType, selectedLevel)
  const prevShowGuidedDiveToggleRef = useRef(showGuidedDiveToggle)

  // Reset level when provider changes
  useEffect(() => {
    setValue('level', '')
  }, [selectedType, setValue])

  // Reset guided dive toggle when eligibility transitions from true to false
  useEffect(() => {
    if (prevShowGuidedDiveToggleRef.current && !showGuidedDiveToggle) {
      setGuidedDiveToggle(false)
    }
    prevShowGuidedDiveToggleRef.current = showGuidedDiveToggle
  }, [showGuidedDiveToggle])

  const handleUploadComplete = useCallback((url: string) => {
    documentUrlRef.current = url
    setIsUploading(false)
    setUploadError(false)
  }, [])

  const handleUploadStart = useCallback(() => {
    setIsUploading(true)
    setUploadError(false)
  }, [])

  const handleUploadError = useCallback(() => {
    setIsUploading(false)
    setUploadError(true)
  }, [])

  const handleGuidedDiveDocsReady = useCallback(
    (docs: Database.GuidedDiveSubmissionDocs | null) => {
      guidedDiveDocsRef.current = docs
    },
    []
  )

  const onSubmit = async (data: FormValues) => {
    if (!userId) return
    if (!documentUrlRef.current) {
      setSubmitError(
        'Please upload a certification document before submitting.'
      )
      return
    }

    if (guidedDiveToggle && !guidedDiveDocsRef.current) {
      setSubmitError(
        'Please complete all professional requirements for guided dives before submitting.'
      )
      return
    }

    setSubmitError(null)
    setIsSubmitting(true)

    const submissionData: Database.AccreditationSubmissionData = {
      type: data.type as AccreditationProvider,
      level: data.level,
      fullName: data.fullName.trim(),
      dateOfBirth: new Date(data.dateOfBirth),
      certificationNumber: data.certificationNumber.trim(),
      optionalMemberId: data.optionalMemberId.trim() || null,
      documentUrl: documentUrlRef.current
    }

    if (providerRequiresEmail(data.type)) {
      submissionData.registeredEmail = data.registeredEmail.trim()
    }

    if (guidedDiveToggle && guidedDiveDocsRef.current) {
      submissionData.guidedDiveDocuments = guidedDiveDocsRef.current
    }

    const guidedDiveOptions = guidedDiveToggle
      ? { guidedDiveFlag: true as const }
      : undefined

    try {
      if (mode === 'resubmit') {
        await resubmitAccreditation(userId, submissionData, guidedDiveOptions)
      } else {
        await submitAccreditation(userId, submissionData, guidedDiveOptions)
      }

      setSubmitSuccess(true)

      // Optimistically update the store so the home page shows "pending" immediately
      if (profile) {
        setProfile({
          ...profile,
          ...(guidedDiveToggle ? { guidedDiveFlag: true } : {}),
          accreditation: {
            type: data.type as AccreditationProvider,
            level: data.level,
            fullName: data.fullName.trim(),
            dateOfBirth: new Date(data.dateOfBirth),
            certificationNumber: data.certificationNumber.trim(),
            optionalMemberId: data.optionalMemberId.trim() || null,
            documentUrl: documentUrlRef.current!,
            status: 'pending',
            submittedAt: new Date(),
            reviewedAt: null,
            reviewedBy: null,
            ...(providerRequiresEmail(data.type)
              ? { registeredEmail: data.registeredEmail.trim() }
              : {})
          }
        })
      }

      setTimeout(() => {
        navigate(routes.home)
      }, 2000)
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Submission failed. Please try again.'
      setSubmitError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  const validateDateOfBirth = (value: string) => {
    if (!value) return 'Date of birth is required'
    const date = new Date(value)
    if (isNaN(date.getTime())) return 'Invalid date'
    const now = new Date()
    if (date >= now) return 'Date of birth must be in the past'
    const maxAge = new Date()
    maxAge.setFullYear(maxAge.getFullYear() - 120)
    if (date < maxAge) return 'Date of birth cannot be more than 120 years ago'
    return true
  }

  if (submitSuccess) {
    return (
      <Alert severity='success' sx={{ my: 2 }}>
        Your accreditation has been submitted successfully and is pending
        review. Redirecting...
      </Alert>
    )
  }

  return (
    <Box component='form' onSubmit={handleSubmit(onSubmit)} noValidate>
      <Typography variant='h6' gutterBottom>
        {mode === 'resubmit'
          ? 'Resubmit Accreditation'
          : 'Submit Accreditation'}
      </Typography>

      {submitError && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      {/* Provider Select */}
      <FormControl fullWidth margin='normal' error={!!errors.type}>
        <InputLabel id='provider-label'>Provider</InputLabel>
        <Controller
          name='type'
          control={control}
          rules={{ required: 'Provider is required' }}
          render={({ field }) => (
            <Select {...field} labelId='provider-label' label='Provider'>
              {PROVIDERS.map(p => (
                <MenuItem key={p} value={p}>
                  {PROVIDER_LABELS[p]}
                </MenuItem>
              ))}
            </Select>
          )}
        />
        {errors.type && <FormHelperText>{errors.type.message}</FormHelperText>}
      </FormControl>

      {/* Level Select (conditional on provider selection) */}
      {selectedType && (
        <FormControl fullWidth margin='normal' error={!!errors.level}>
          <InputLabel id='level-label'>Level</InputLabel>
          <Controller
            name='level'
            control={control}
            rules={{ required: 'Level is required' }}
            render={({ field }) => (
              <Select {...field} labelId='level-label' label='Level'>
                {getLevelsForProvider(selectedType).map(level => (
                  <MenuItem key={level} value={level}>
                    {level}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
          {errors.level && (
            <FormHelperText>{errors.level.message}</FormHelperText>
          )}
        </FormControl>
      )}

      {/* Guided Dive Toggle (conditional on Divemaster level) */}
      {showGuidedDiveToggle && (
        <FormControlLabel
          control={
            <Switch
              checked={guidedDiveToggle}
              onChange={e => setGuidedDiveToggle(e.target.checked)}
            />
          }
          label='I want to offer guided dives'
          sx={{ mt: 1, mb: 1 }}
        />
      )}

      {/* Guided Dive Professional Requirements (conditional on toggle) */}
      {guidedDiveToggle && userId && (
        <GuidedDiveRequirements
          userId={userId}
          provider={selectedType}
          disabled={isSubmitting}
          onDocsReady={handleGuidedDiveDocsReady}
        />
      )}

      {/* Full Name */}
      <TextField
        fullWidth
        label='Full Name'
        margin='normal'
        error={!!errors.fullName}
        helperText={errors.fullName?.message}
        {...register('fullName', {
          required: 'Full name is required',
          minLength: { value: 1, message: 'Full name is required' },
          maxLength: {
            value: 100,
            message: 'Full name must be 100 characters or fewer'
          }
        })}
      />

      {/* Date of Birth */}
      <TextField
        fullWidth
        label='Date of Birth'
        type='date'
        margin='normal'
        slotProps={{ inputLabel: { shrink: true } }}
        error={!!errors.dateOfBirth}
        helperText={errors.dateOfBirth?.message}
        {...register('dateOfBirth', {
          validate: validateDateOfBirth
        })}
      />

      {/* Certification Number */}
      <TextField
        fullWidth
        label='Certification Number'
        margin='normal'
        error={!!errors.certificationNumber}
        helperText={errors.certificationNumber?.message}
        {...register('certificationNumber', {
          required: 'Certification number is required',
          minLength: { value: 1, message: 'Certification number is required' },
          maxLength: {
            value: 50,
            message: 'Certification number must be 50 characters or fewer'
          }
        })}
      />

      {/* Optional Member ID */}
      <TextField
        fullWidth
        label='Member/Professional Number (optional)'
        margin='normal'
        error={!!errors.optionalMemberId}
        helperText={errors.optionalMemberId?.message}
        {...register('optionalMemberId', {
          maxLength: { value: 50, message: 'Must be 50 characters or fewer' }
        })}
      />

      {/* Registered Email (only for providers that require it) */}
      {providerRequiresEmail(selectedType) && (
        <TextField
          fullWidth
          label='Registered Email Address'
          type='email'
          margin='normal'
          error={!!errors.registeredEmail}
          helperText={errors.registeredEmail?.message}
          {...register('registeredEmail', {
            required: 'Registered email is required for PADI',
            maxLength: {
              value: 254,
              message: 'Email must be 254 characters or fewer'
            },
            pattern: {
              value:
                /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
              message: 'Please enter a valid email address'
            }
          })}
        />
      )}

      {/* Certificate Document Upload */}
      {userId && (
        <CertDocumentUpload
          userId={userId}
          onUploadComplete={handleUploadComplete}
          onUploadStart={handleUploadStart}
          onUploadError={handleUploadError}
          disabled={isSubmitting}
        />
      )}

      {uploadError && (
        <FormHelperText error>
          Document upload failed. Please retry the upload above.
        </FormHelperText>
      )}

      {/* Submit Button */}
      <Button
        type='submit'
        variant='contained'
        fullWidth
        sx={{ mt: 2 }}
        disabled={isSubmitting || isUploading}
      >
        {isSubmitting
          ? 'Submitting...'
          : mode === 'resubmit'
          ? 'Resubmit Accreditation'
          : 'Submit Accreditation'}
      </Button>
    </Box>
  )
}

export default AccreditationForm
